// CRM 2.0 · M12 Template Builder · Edit in Word (DC-206): the version's Word file in the ONLYOFFICE editor, with the
// quick fixes already in it. Legal edits freely, like Word. Save = a new draft version by the Replace file path (the
// same scan; blank settings carried over, unmatched ones listed), then back to Map fields of the new version.
// While it is open, Map fields is locked for this version (one editor at a time).
import React, { useEffect, useRef, useState } from "react";
import cls from "./Templates.module.css";
import { openEditInWord, saveEditInWord, closeEditInWord } from "../../Services/Templates";
import { errorTost, infoTost } from "../../Services/Toast";
import { fmtDate } from "./templateCommon";

const EDITOR_ID = "crm2-word-editor";

// ONLYOFFICE's JS API comes from the ONLYOFFICE server itself, once per page
function loadDocsApi(docsUrl) {
  if (window.DocsAPI) return Promise.resolve();
  return new Promise((ok, fail) => {
    const s = document.createElement("script");
    s.src = `${docsUrl}/web-apps/apps/api/documents/api.js`;
    s.onload = () => ok();
    s.onerror = () => fail(new Error("The editor could not be reached. Is ONLYOFFICE running?"));
    document.body.appendChild(s);
  });
}

function EditInWord({ versionId, version, canEdit, onSaved, onClosed }) {
  const editor = useRef(null);
  const [state, setState] = useState("idle"); // idle | opening | open | saving
  const lock = version.wordEdit;
  const mine = !!lock?.mine;
  const othersLock = lock && !mine;

  const destroy = () => {
    if (editor.current) {
      try { editor.current.destroyEditor(); } catch (e) { /* already gone */ }
      editor.current = null;
    }
  };
  useEffect(() => destroy, []);

  // leaving the page with the editor open: the browser asks first
  useEffect(() => {
    if (state !== "open" && state !== "saving") return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state]);

  async function open() {
    setState("opening");
    try {
      const { docsUrl, config } = await openEditInWord(versionId);
      await loadDocsApi(docsUrl);
      setState("open");
      // the editor div is on the page once the state is "open"
      setTimeout(() => {
        destroy();
        editor.current = new window.DocsAPI.DocEditor(EDITOR_ID, {
          ...config,
          width: "100%",
          height: "100%",
          events: { onError: (e) => errorTost(`Editor: ${e?.data?.errorDescription || e?.data?.errorCode || "error"}`) },
        });
      }, 0);
    } catch (e) {
      setState("idle");
      errorTost(e.message);
    }
  }

  async function save() {
    setState("saving");
    try {
      const r = await saveEditInWord(versionId);
      destroy();
      setState("idle");
      if (r.unchanged) {
        infoTost(r.notOpen ? "The editor was closed meanwhile; nothing was saved" : "No changes to save; Map fields is open again");
        onClosed();
        return;
      }
      onSaved(r);
    } catch (e) {
      setState("open");
      errorTost(e.message);
    }
  }

  async function close() {
    destroy();
    setState("idle");
    try {
      await closeEditInWord(versionId);
    } catch (e) {
      errorTost(e.message);
    }
    onClosed();
  }

  if (!version.wordEditReady) {
    return <div className={cls.panel}><p className={cls.hint}>Edit in Word is not set up on this server yet.</p></div>;
  }

  return (
    <div className={cls.panel}>
      <div className={cls.actionbar}>
        <h2 className={cls.panelTitle} style={{ margin: 0 }}>Edit in Word</h2>
        {(state === "open" || state === "saving") && (
          <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap" }}>
            <button className={cls.btn} onClick={close} disabled={state === "saving"}>Close without saving</button>
            <button className={`${cls.btn} ${cls.primary}`} onClick={save} disabled={state === "saving"}>
              {state === "saving" ? "Saving… making the new version" : "Save and go back to Map fields"}
            </button>
          </div>
        )}
      </div>

      {state === "idle" || state === "opening" ? (
        <>
          <p className={cls.hint}>
            Opens this version's Word file in the editor, with your page fixes already in it. Change or remove wording, add or
            remove clauses, type in empty cells, change margins, page breaks and tables — as in Word.
            Save makes a new draft version: the file is scanned again and your blank settings carry over; any that cannot be
            matched are listed to re-check or re-assign.
          </p>
          <p className={cls.hint}>While the editor is open, Map fields is locked for this version.</p>
          {othersLock && (
            <div className={cls.blockBox}>{lock.byName || "Someone"} has this version open in Edit in Word since {fmtDate(lock.at)}.</div>
          )}
          {canEdit && !othersLock && (
            <button className={`${cls.btn} ${cls.primary}`} onClick={open} disabled={state === "opening"}>
              {state === "opening" ? "Opening the editor…" : mine ? "Open the editor again" : "Open in editor"}
            </button>
          )}
        </>
      ) : (
        <div style={{ height: "80vh", border: "1px solid var(--g200)", borderRadius: ".8rem", overflow: "hidden" }}>
          <div id={EDITOR_ID} />
        </div>
      )}
    </div>
  );
}

export default EditInWord;
